/**
 * Monogram avatar — the categorical identity tile from DESIGN.md `avatarPalette`,
 * used on Vendor / Device / User / Company rows, drawers and profiles.
 *
 * **It is identity, never status.** The ten hues distinguish one subject from another so a
 * long directory scans by shape and colour instead of by reading every name. They encode
 * nothing about health, which is exactly why a tile can sit beside a status pill without
 * competing with it — the pill owns meaning, the tile owns identity. Adding a status tint
 * here would break that separation and introduce a sixth status hue by the back door.
 *
 * **Why a hash and not the spec's index.** DESIGN.md describes the palette as "cycled by
 * `(id-1) % 10`", which assumes the prototype's small integer ids. Real ids here are UUIDs,
 * so there is no ordinal to cycle. `hueFor` hashes the id string instead, preserving the
 * property the spec actually depends on — a given subject is always the same colour, and
 * the ten hues stay roughly evenly used — without inventing an ordering that does not exist.
 */

/** Palette size — must match the `--avatar-N` token count in tokens.css. */
const HUES = 10;

/**
 * Deterministic id → hue index in `1..HUES`.
 *
 * FNV-1a over the id's code units: cheap, dependency-free, and well spread over the hex
 * characters that dominate a UUID (a naive char-sum clusters badly there, because UUIDs
 * share their alphabet and length). `>>> 0` keeps the running value an unsigned 32-bit int
 * so the result never depends on sign behaviour.
 */
export function hueFor(id: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < id.length; i += 1) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return (h % HUES) + 1;
}

/**
 * Initials for the tile — at most two, from the first two whitespace-separated words.
 *
 * Falls back to the first character when there is only one word, and to `?` when there is
 * nothing usable, so the tile never renders empty and never throws on unexpected data.
 * Uppercasing is left to CSS (`text-transform`) so the accessible name keeps the original
 * casing of the label it accompanies.
 */
export function monogram(label: string): string {
  const words = label.trim().split(/\s+/).filter(Boolean);
  // Destructured rather than indexed, and `charAt` rather than `[0]`: this package builds
  // under `noUncheckedIndexedAccess`, where every index read widens to `| undefined`.
  const [first, second] = words;
  if (first === undefined) return '?';
  if (second === undefined) return first.slice(0, 2);
  return first.charAt(0) + second.charAt(0);
}

/**
 * The tile itself.
 *
 * `aria-hidden` by default: an avatar accompanies a visible text label, so exposing its
 * initials to a screen reader would just stutter the name that follows it. Pass a `label`
 * only when the tile stands alone with no adjacent text.
 */
export function Avatar({
  id,
  name,
  label,
  size,
}: {
  /** Stable subject id — drives the hue, so the same subject keeps its colour. */
  id: string;
  /** Display name the monogram is derived from. */
  name: string;
  /** Accessible name. Omit when adjacent text already names the subject. */
  label?: string;
  /**
   * Tile size. Default (omitted) is the 30px row tile; `'lg'` is the 44px page-header
   * tile. A variant rather than a free px value, so the two sizes stay in CSS beside the
   * radius that has to change with them.
   */
  size?: 'lg';
}): React.JSX.Element {
  return (
    <span
      className="avatar"
      {...(size === undefined ? {} : { 'data-size': size })}
      data-hue={hueFor(id)}
      {...(label === undefined ? { 'aria-hidden': true } : { role: 'img', 'aria-label': label })}
    >
      {monogram(name)}
    </span>
  );
}
